import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * S-ADMINLOOKUP-001: repository suggestion shape returned by the lookup.
 * `fullName` is the org/repo display/value text (AC-3); `defaultBranch` is
 * passed upward on selection so the parent can auto-fill the branch field
 * (AC-6). See component-specs-s-adminlookup-001.md Section 3.
 */
export interface RepoSuggestion {
  fullName: string;
  defaultBranch: string;
}

export interface RepoAutocompleteProps {
  /** Controlled repo field value. */
  value: string;
  /** Propagates repo keystrokes to the parent (OnboardForm). */
  onChange: (value: string) => void;
  /**
   * Emitted on selection (click or Enter on a highlighted row), AC-5/AC-6/AC-7.
   * RepoAutocomplete does NOT own branchManuallyEdited — the parent decides
   * whether to apply the autofill (ADR-S-ADMINLOOKUP-001 Decision 2).
   */
  onSelect: (repo: RepoSuggestion) => void;
  /** Minimum characters before a lookup fires. Default 2 (AC-2). */
  minChars?: number;
  /** Debounce window in ms. Default 300 (AC-1). */
  debounceMs?: number;
  /** Client-side timeout in ms. Default 5000 (AC-12). */
  timeoutMs?: number;
  /** Hits GET /api/search/repositories?q=<value> (AC-1). */
  fetchSuggestions: (query: string) => Promise<RepoSuggestion[]>;
}

/**
 * S-ADMINLOOKUP-001 — ARIA combobox that augments the existing
 * `onboard-repo-input` text field with a debounced repository lookup
 * dropdown. Implements component-specs-s-adminlookup-001.md verbatim:
 * locked testids `onboard-repo-input` (preserved), `admin-repo-autocomplete-dropdown`,
 * `admin-repo-autocomplete-item` (single value on every row; the active row is
 * disambiguated via `data-active="true"` + `aria-selected="true"`, never a
 * second data-testid), and `admin-repo-autocomplete-status` (mounts once on
 * first render and never unmounts).
 */
export function RepoAutocomplete({
  value,
  onChange,
  onSelect,
  minChars = 2,
  debounceMs = 300,
  timeoutMs = 5000,
  fetchSuggestions,
}: RepoAutocompleteProps): ReactNode {
  const [suggestions, setSuggestions] = useState<RepoSuggestion[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSettled, setHasSettled] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  // AC-13: stale-response guard — only the most recently dispatched request's
  // outcome is allowed to update state. Each dispatch bumps the token; a
  // settling request checks its token still matches before writing state.
  const requestTokenRef = useRef(0);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Always clear any pending debounce on every value change so a fresh
    // keystroke restarts the 300ms window (AC-1).
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    if (value.length < minChars) {
      // AC-2 / AC-19: below the minimum (including post-clear), no request,
      // no dropdown; status stays mounted but settles to false with no content.
      requestTokenRef.current += 1; // invalidate any in-flight request
      setSuggestions([]);
      setDropdownOpen(false);
      setActiveIndex(null);
      setIsLoading(false);
      setHasSettled(false);
      return;
    }

    const myToken = ++requestTokenRef.current;
    setHasSettled(true); // status element now has "settle" semantics for this value
    setIsLoading(true);

    debounceTimerRef.current = setTimeout(() => {
      let timedOut = false;
      const timeoutHandle = setTimeout(() => {
        timedOut = true;
        if (requestTokenRef.current === myToken) {
          // AC-12: client-side 5000ms timeout — treat as failure.
          setSuggestions([]);
          setDropdownOpen(false);
          setActiveIndex(null);
          setIsLoading(false);
        }
      }, timeoutMs);

      fetchSuggestions(value)
        .then((results) => {
          if (timedOut || requestTokenRef.current !== myToken) return; // AC-13
          clearTimeout(timeoutHandle);
          setSuggestions(results);
          setDropdownOpen(results.length > 0); // AC-3 / AC-4
          setActiveIndex(null);
          setIsLoading(false);
        })
        .catch(() => {
          if (timedOut || requestTokenRef.current !== myToken) return; // AC-13
          clearTimeout(timeoutHandle);
          // AC-12: network error / non-200 — no dropdown, no blocking error.
          setSuggestions([]);
          setDropdownOpen(false);
          setActiveIndex(null);
          setIsLoading(false);
        });
    }, debounceMs);

    return () => {
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchSuggestions/minChars/debounceMs/timeoutMs are stable across the component's lifetime in this story's usage.
  }, [value]);

  function selectSuggestion(repo: RepoSuggestion): void {
    onChange(repo.fullName); // AC-5
    setDropdownOpen(false);
    setActiveIndex(null);
    onSelect(repo); // AC-6 — parent decides whether to apply branch autofill (AC-7)
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (!dropdownOpen) return; // AC-10: arrows/enter/escape are no-ops when closed

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => {
        if (prev === null) return 0; // first ArrowDown highlights the first item
        return Math.min(prev + 1, suggestions.length - 1);
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => {
        if (prev === null) return suggestions.length - 1;
        return Math.max(prev - 1, 0);
      });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Enter with nothing highlighted is a no-op: the user hasn't arrowed to
      // a suggestion yet, so committing the first match now would onboard a
      // repo they never actually looked at. This also blocks an accidental
      // native form submit before the list has been reviewed (spec Section 4).
      if (activeIndex === null) return;
      const repo = suggestions[activeIndex];
      if (repo) selectSuggestion(repo);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setDropdownOpen(false);
      setActiveIndex(null); // AC-10: no field changes, no selection
    }
  }

  function handleBlur(): void {
    // AC-11: blur closes the dropdown without requiring a selection. Deferred
    // so a mousedown-triggered click selection on a dropdown item still fires
    // before the dropdown unmounts.
    window.setTimeout(() => {
      setDropdownOpen(false);
      setActiveIndex(null);
    }, 0);
  }

  const activeItemId = activeIndex !== null ? `repo-option-${activeIndex}` : undefined;
  const statusLoading = isLoading ? 'true' : 'false';
  const statusContent = !hasSettled ? null : isLoading ? 'Searching…' : null;

  return (
    <div className="repo-autocomplete">
      <input
        className="onboard-repo-input"
        data-testid="onboard-repo-input"
        placeholder="org/repo"
        role="combobox"
        aria-expanded={dropdownOpen}
        aria-controls="admin-repo-autocomplete-dropdown"
        aria-autocomplete="list"
        aria-activedescendant={activeItemId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        aria-label="Repository (org/repo format)"
      />

      {/* Assistive tech only picks up aria-live updates on a node that is
          already being observed, so this span must stay mounted at all
          times — toggling it in/out of the DOM would risk the screen reader
          missing the status change entirely. */}
      <span
        data-testid="admin-repo-autocomplete-status"
        data-loading={statusLoading}
        role="status"
        aria-live="polite"
        className="repo-autocomplete__status"
      >
        {statusContent}
      </span>

      {dropdownOpen && suggestions.length > 0 && (
        <ul
          id="admin-repo-autocomplete-dropdown"
          data-testid="admin-repo-autocomplete-dropdown"
          role="listbox"
          className="repo-autocomplete__dropdown"
        >
          {suggestions.map((repo, i) => (
            <li
              key={repo.fullName}
              id={`repo-option-${i}`}
              data-testid="admin-repo-autocomplete-item"
              data-active={i === activeIndex ? 'true' : undefined}
              role="option"
              aria-selected={i === activeIndex}
              className={
                i === activeIndex
                  ? 'repo-autocomplete__item repo-autocomplete__item--active'
                  : 'repo-autocomplete__item'
              }
              onMouseDown={(e) => {
                // onMouseDown (not onClick) fires before the input's onBlur,
                // so the selection is committed before handleBlur closes the
                // dropdown (Flow C).
                e.preventDefault();
                selectSuggestion(repo);
              }}
            >
              {repo.fullName}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
